import React, { useState, useEffect } from 'react';
import { 
  UserGroupIcon,
  ClockIcon,
  CalendarIcon,
  CurrencyPoundIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { attendanceAPI, leaveAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Dashboard = () => {
  const [dashboardStats, setDashboardStats] = useState(null);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [upcomingLeave, setUpcomingLeave] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { user, isEmployee, isManager, isAdmin } = useAuth();

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // Fetch dashboard stats from the correct endpoint
        const statsResponse = await fetch('/api/dashboard/stats', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!statsResponse.ok) {
          throw new Error('Failed to fetch dashboard stats');
        }
        
        const statsData = await statsResponse.json();
        setDashboardStats(statsData.stats);

        // Fetch today's attendance for employees
        if (isEmployee()) {
          const attendanceResponse = await attendanceAPI.getToday();
          setTodayAttendance(attendanceResponse.data);
        }

        // Fetch leave balance for employees
        if (isEmployee()) {
          try {
            const balanceResponse = await leaveAPI.getBalance();
            setLeaveBalance(balanceResponse.data);
          } catch (leaveError) {
            console.log('Leave balance not available:', leaveError);
            setLeaveBalance(null);
          }
        }

        // Fetch upcoming leave
        try {
          const upcomingResponse = await leaveAPI.getUpcoming();
          setUpcomingLeave(upcomingResponse.data.upcoming_leave || []);
        } catch (leaveError) {
          console.log('Upcoming leave not available:', leaveError);
          setUpcomingLeave([]);
        }

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [isEmployee]);

  const handleClockAction = async (action) => {
    try {
      let response;
      switch (action) {
        case 'clockIn':
          response = await attendanceAPI.clockIn();
          break;
        case 'clockOut':
          response = await attendanceAPI.clockOut();
          break;
        case 'startBreak':
          response = await attendanceAPI.startBreak();
          break;
        case 'endBreak':
          response = await attendanceAPI.endBreak();
          break;
        default:
          throw new Error('Invalid action');
      }

      // Refresh dashboard data after clock action
      window.location.reload();
    } catch (error) {
      console.error('Error performing clock action:', error);
      alert('Failed to perform action. Please try again.');
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '--:--';
    return timeString.substring(0, 5);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      present: { bg: 'bg-green-100', text: 'text-green-800', label: 'Present' },
      absent: { bg: 'bg-red-100', text: 'text-red-800', label: 'Absent' },
      late: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Late' },
      'half-day': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Half Day' },
    };
    
    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Error</h3>
          <p className="mt-1 text-sm text-gray-500">{error}</p>
          <div className="mt-6">
            <button
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Welcome back, {user?.first_name || user?.email}!
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      {dashboardStats && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <UserGroupIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Employees
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {dashboardStats.totalEmployees || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Present Today
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {dashboardStats.presentToday || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CalendarIcon className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      On Leave
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {dashboardStats.onLeave || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Pending Requests
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {dashboardStats.pendingRequests || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Today's Attendance for Employees */}
      {isEmployee() && todayAttendance && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Today's Attendance</h2>
          </div>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <p className="text-lg font-medium text-gray-900">
                  {todayAttendance.attendance?.status || 'Not clocked in'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Clock In</p>
                <p className="text-lg font-medium text-gray-900">
                  {formatTime(todayAttendance.attendance?.clock_in)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Clock Out</p>
                <p className="text-lg font-medium text-gray-900">
                  {formatTime(todayAttendance.attendance?.clock_out)}
                </p>
              </div>
            </div>
            
            {/* Clock Actions */}
            <div className="mt-6 flex space-x-3">
              {todayAttendance.can_clock_in && (
                <button
                  onClick={() => handleClockAction('clockIn')}
                  className="btn-primary"
                >
                  Clock In
                </button>
              )}
              {todayAttendance.can_clock_out && (
                <button
                  onClick={() => handleClockAction('clockOut')}
                  className="btn-secondary"
                >
                  Clock Out
                </button>
              )}
              {todayAttendance.can_start_break && (
                <button
                  onClick={() => handleClockAction('startBreak')}
                  className="btn-outline"
                >
                  Start Break
                </button>
              )}
              {todayAttendance.can_end_break && (
                <button
                  onClick={() => handleClockAction('endBreak')}
                  className="btn-primary"
                >
                  End Break
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Leave Balance for Employees */}
      {isEmployee() && leaveBalance && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Leave Balance</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <p className="text-sm text-gray-500">Annual Leave</p>
                <p className="text-lg font-medium text-gray-900">
                  {leaveBalance.annual_leave || 0} days
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Sick Leave</p>
                <p className="text-lg font-medium text-gray-900">
                  {leaveBalance.sick_leave || 0} days
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Other Leave</p>
                <p className="text-lg font-medium text-gray-900">
                  {leaveBalance.other_leave || 0} days
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upcoming Leave */}
      {upcomingLeave.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Upcoming Leave</h2>
          </div>
          <div className="overflow-hidden">
            <ul className="divide-y divide-gray-200">
              {upcomingLeave.map((leave, index) => (
                <li key={index} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {leave.employee_name}
                      </p>
                      <p className="text-sm text-gray-500">
                        {leave.leave_type} • {leave.start_date} to {leave.end_date}
                      </p>
                    </div>
                    <div>
                      {getStatusBadge(leave.status)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
